import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { ResourceTypes, Story } from '../../Api/Story/types';
import NewStoryForm, { NewStoryFormRef } from './NewStoryForm';
import { Button } from '@fluentui/react-components';
import ImageSelector from '../../Shared/ImageSelector';
import {
	useAddStoryImageMutation,
	usePostStoryMutation
} from '../../Api/Story/storySlice';
import { StoryForm } from './types ';
import { useAppSelector } from '../../pgStore';
import ResourceSelector from '../../Shared/ResourceSelector';

import styles from './NewStory.module.scss';

export default function NewStory(props: {
	toggleDialog: Dispatch<SetStateAction<boolean>>;
}) {
	const currentUser = useAppSelector((state) => state.userSlice.username);
	const [currentSlide, setCurrentSlide] = useState(1);
	const maxSlideCount = 3;
	const [resource, setResource] = useState<ResourceTypes>();
	const [storyImageUrl, setStoryImageUrl] = useState<string>();
	const formRef = useRef<NewStoryFormRef>(null);
	const [saveStoryImage, { isLoading, isSuccess, data: saveImageResponse }] =
		useAddStoryImageMutation();
	const [saveStory, { isLoading: savingStory, isSuccess: savedStory }] =
		usePostStoryMutation();

	useEffect(() => {
		if (isSuccess && saveImageResponse.url) {
			setStoryImageUrl(saveImageResponse.url);
		}
	}, [isSuccess, saveImageResponse]);

	const handlePrevClick = () => {
		window.location.href = `#slide-${String(currentSlide - 1)}`;
		setCurrentSlide((s) => s - 1);
	};

	const handleNextClick = () => {
		window.location.href = `#slide-${String(currentSlide + 1)}`;
		setCurrentSlide((s) => s + 1);
	};

	const submitStory = (formValues: StoryForm) => {
		if (!currentUser || !resource) {
			throw new Error('Invalid data to submit the form.');
		}

		const data: Partial<Story> = {
			author: currentUser,
			content: formValues.content,
			headline: formValues.header,
			resourceType: resource,
			tags: [],
			imageFileName: storyImageUrl?.split('/').slice(-1)[0] ?? undefined
		};

		void saveStory(data);
	};

	// Close if saved successfully
	useEffect(() => {
		if (savedStory) {
			props.toggleDialog(false);
		}
	}, [props, savedStory]);

	return (
		<div className={styles.root}>
			<div className={styles.slides}>
				<div id='slide-1'>
					<ResourceSelector
						value={resource}
						setValue={setResource}
					/>
				</div>

				<div id='slide-2'>
					<ImageSelector
						imageUrl={storyImageUrl}
						saveImage={(data: FormData) => {
							void saveStoryImage(data);
						}}
						isLoading={isLoading}
					/>
				</div>

				<div id='slide-3'>
					<NewStoryForm
						submit={submitStory}
						ref={formRef}
					/>
				</div>
			</div>

			<div className={styles.actions}>
				{currentSlide > 1 && (
					<Button onClick={handlePrevClick}>Previous</Button>
				)}
				{currentSlide < maxSlideCount && (
					<Button
						onClick={handleNextClick}
						disabled={Boolean(!resource)}
					>
						Next
					</Button>
				)}
				{currentSlide === maxSlideCount && (
					<Button
						appearance='primary'
						onClick={() => formRef.current?.submitForm()}
						disabled={savingStory}
					>
						Save
					</Button>
				)}
			</div>
		</div>
	);
}
